import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './RoleDetailModal.module.css';
import Icon from '../../components/common/Icon/Icon';

function RoleDetailModal({ isOpen, role, auditionTitle, appliedRoleIds, isCheckingApplied, isExpired, onClose, onApply }) {
  const hasApplied = appliedRoleIds?.has(String(role?.id)) ?? false;
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !role) return null;

  const isDisabled = hasApplied || isCheckingApplied || isExpired;
  const roleMeta = [
    role.roleType || null,
    role.gender && role.gender !== '무관' ? role.gender : null,
    role.ageRange && role.ageRange !== '제한 없음' ? role.ageRange : null,
    role.headcount ? `${role.headcount}명 모집` : null,
    role.heightMin ? `최소 ${role.heightMin}cm` : null,
  ].filter(Boolean);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-detail-modal-title"
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <h2 id="role-detail-modal-title" className={styles.title}>모집 역할 상세</h2>
            {auditionTitle && (
              <p className={styles.subtitle}>{auditionTitle}의 배역 정보입니다.</p>
            )}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="모달 닫기"
          >
            <Icon name="close" size="20" />
          </button>
        </div>

        <div className={styles.body}>
          <section className={styles.heroSection}>
            <div className={styles.heroHeader}>
              <div>
                <strong className={styles.roleName}>{role.name}</strong>
                {role.payment && <p className={styles.payment}>{role.payment}</p>}
              </div>
            </div>

            {roleMeta.length > 0 && (
              <div className={styles.tags}>
                {roleMeta.map((item) => (
                  <span key={item} className={styles.tag}>{item}</span>
                ))}
              </div>
            )}
          </section>

          {role.description && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>배역 설명</h3>
              <p className={styles.description}>
                {role.description}
              </p>
            </section>
          )}

          {role.shootingPeriod && role.shootingPeriod !== '추후 공지' && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>촬영일</h3>
              <p className={styles.description}>{role.shootingPeriod}</p>
            </section>
          )}

          {role.requiredSkills && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>우대 사항</h3>
              <p className={styles.description}>{role.requiredSkills}</p>
            </section>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
          >
            닫기
          </button>
          <button
            type="button"
            className={`${styles.applyButton} ${isDisabled ? styles.disabledButton : ''}`}
            onClick={() => {
              if (isDisabled) return;
              onClose();
              onApply?.(role.id);
            }}
            disabled={isDisabled}
          >
            {isCheckingApplied ? '확인 중...' : hasApplied ? '지원 완료' : isExpired ? '지원 마감' : '해당 배역 지원하기'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RoleDetailModal;
